import React, { useState } from 'react';
import { X, RefreshCw } from 'lucide-react';

interface VideoJob {
  id: string;
  filename: string;
  originalSize: number;
  compressedSize?: number;
  status: string;
  progress: number;
  createdAt: string;
  processingTime?: number;
  estimatedTimeRemaining?: number;
  outputUrl?: string;
  previewUrl?: string;
  error?: string;
  codec?: string;
  targetSizeMB?: number;
}

interface RedoConversionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config: RedoConfig) => void;
  job: VideoJob | null;
}

export interface RedoConfig {
  codec: 'h264' | 'h265';
  targetSizeMB?: number;
  width?: number;
  height?: number;
  normalizeAudio?: boolean;
}

const RedoConversionModal: React.FC<RedoConversionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  job
}) => {
  const [config, setConfig] = useState<RedoConfig>({
    codec: 'h264',
    normalizeAudio: false,
  });

  const [targetSizeInput, setTargetSizeInput] = useState('');
  const [widthInput, setWidthInput] = useState('');
  const [heightInput, setHeightInput] = useState('');
  const [errors, setErrors] = useState<{ targetSize?: string; dim?: string }>({});

  const validateTargetSize = (value: string) => {
    if (value === '') return true;

    const size = parseInt(value);
    if (isNaN(size) || size < 1) {
      setErrors({ targetSize: 'Tamanho deve ser um número válido maior que 0' });
      return false;
    }

    if (size > 1000) {
      setErrors({ targetSize: 'Tamanho não pode exceder 1000 MB' });
      return false;
    }

    if (job && size >= (job.originalSize / (1024 * 1024))) {
      setErrors({ targetSize: 'Tamanho deve ser menor que o arquivo original' });
      return false;
    }

    setErrors({});
    return true;
  };

  const handleTargetSizeChange = (value: string) => {
    setTargetSizeInput(value);
    validateTargetSize(value);
  };

  const handleConfirm = () => {
    if (!validateTargetSize(targetSizeInput)) return;

    const width = widthInput ? parseInt(widthInput, 10) : undefined;
    const height = heightInput ? parseInt(heightInput, 10) : undefined;
    if (width !== undefined && (width < 16 || width > 7680)) {
      setErrors(prev => ({ ...prev, dim: 'Largura entre 16 e 7680' }));
      return;
    }
    if (height !== undefined && (height < 16 || height > 7680)) {
      setErrors(prev => ({ ...prev, dim: 'Altura entre 16 e 7680' }));
      return;
    }

    const finalConfig: RedoConfig = {
      codec: config.codec,
      targetSizeMB: targetSizeInput ? parseInt(targetSizeInput) : undefined,
      width,
      height,
      normalizeAudio: !!config.normalizeAudio,
    };

    onConfirm(finalConfig);
    onClose();

    setConfig({ codec: 'h264', normalizeAudio: false });
    setTargetSizeInput('');
    setWidthInput('');
    setHeightInput('');
    setErrors({});
  };

  const handleClose = () => {
    onClose();
    setConfig({ codec: 'h264', normalizeAudio: false });
    setTargetSizeInput('');
    setWidthInput('');
    setHeightInput('');
    setErrors({});
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const getCompressionEstimate = () => {
    if (!targetSizeInput || !job) return null;

    const targetSize = parseInt(targetSizeInput);
    const originalMB = job.originalSize / (1024 * 1024);
    const ratio = ((originalMB - targetSize) / originalMB) * 100;

    return ratio > 0 ? ratio.toFixed(1) : null;
  };

  if (!isOpen || !job) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="retro-card max-w-lg w-full border-4 border-retro-accent shadow-retro-2">
        {/* Header */}
        <div className="p-4 border-b-2 border-retro-line bg-retro-surface-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className="text-retro-accent" size={20} />
              <h2 className="text-retro-pixel text-lg text-retro-accent">
                REFAZER CONVERSÃO
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="retro-button retro-button-secondary px-3 py-2"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* File Info */}
          <div className="retro-card p-3 bg-retro-bg border-2 border-retro-line">
            <h3 className="text-retro-pixel text-xs text-retro-primary mb-2">
              ARQUIVO SELECIONADO
            </h3>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-retro-text">{job.filename}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-retro-muted">Tamanho original:</span>
                <span className="text-retro-text">{formatFileSize(job.originalSize)}</span>
              </div>
              {job.compressedSize && (
                <div className="flex justify-between text-xs">
                  <span className="text-retro-muted">Tamanho atual:</span>
                  <span className="text-retro-text">{formatFileSize(job.compressedSize)}</span>
                </div>
              )}
              {job.codec && (
                <div className="flex justify-between text-xs">
                  <span className="text-retro-muted">Codec atual:</span>
                  <span className="text-retro-text">{job.codec.toUpperCase()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Codec Selection */}
          <div>
            <label className="block text-sm text-retro-accent mb-2">
              NOVO CODEC
            </label>
            <div className="space-y-2">
              <label className={`
                retro-card p-3 border-2 cursor-pointer transition-all duration-200 flex items-center gap-3
                ${config.codec === 'h264'
                  ? 'border-retro-accent bg-retro-accent bg-opacity-10'
                  : 'border-retro-line hover:border-retro-primary'
                }
              `}>
                <input
                  type="radio"
                  name="codec"
                  value="h264"
                  checked={config.codec === 'h264'}
                  onChange={(e) => setConfig(prev => ({ ...prev, codec: e.target.value as 'h264' | 'h265' }))}
                />
                <div>
                  <div className={`text-sm font-medium ${config.codec === 'h264' ? 'text-black' : 'text-retro-text'}`}>
                    H.264 (AVC)
                  </div>
                  <div className={`text-xs ${config.codec === 'h264' ? 'text-black' : 'text-retro-muted'}`}>
                    Máxima compatibilidade
                  </div>
                </div>
              </label>

              <label className={`
                retro-card p-3 border-2 cursor-pointer transition-all duration-200 flex items-center gap-3
                ${config.codec === 'h265'
                  ? 'border-retro-accent bg-retro-accent bg-opacity-10'
                  : 'border-retro-line hover:border-retro-primary'
                }
              `}>
                <input
                  type="radio"
                  name="codec"
                  value="h265"
                  checked={config.codec === 'h265'}
                  onChange={(e) => setConfig(prev => ({ ...prev, codec: e.target.value as 'h264' | 'h265' }))}
                />
                <div>
                  <div className={`text-sm font-medium ${config.codec === 'h265' ? 'text-black' : 'text-retro-text'}`}>
                    H.265 (HEVC)
                  </div>
                  <div className={`text-xs ${config.codec === 'h265' ? 'text-black' : 'text-retro-muted'}`}>
                    Melhor eficiência
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Target Size */}
          <div>
            <label className="block text-sm text-retro-accent mb-2">
              NOVO TAMANHO ALVO (MB) - Opcional
            </label>
            <input
              type="number"
              min="1"
              max="1000"
              value={targetSizeInput}
              onChange={(e) => handleTargetSizeChange(e.target.value)}
              className={`retro-input w-full px-3 py-2 ${errors.targetSize ? 'border-retro-error' : ''}`}
              placeholder="Deixe vazio para compressão automática"
            />
            {errors.targetSize && (
              <p className="text-retro-error text-xs mt-1">❌ {errors.targetSize}</p>
            )}

            {getCompressionEstimate() && (
              <div className="retro-card p-2 mt-2 bg-retro-accent bg-opacity-10 border-2 border-retro-accent">
                <p className="text-xs text-retro-text">
                  📊 Compressão estimada: <span className="text-black font-medium">{getCompressionEstimate()}%</span>
                </p>
              </div>
            )}

            <p className="text-retro-muted text-xs mt-1">
              Nova versão será criada como filho deste arquivo
            </p>
          </div>

          <div>
            <label className="block text-sm text-retro-accent mb-2">
              RESOLUÇÃO (px) — opcional
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="16"
                max="7680"
                value={widthInput}
                onChange={(e) => {
                  setWidthInput(e.target.value);
                  setErrors(prev => ({ ...prev, dim: undefined }));
                }}
                className="retro-input w-full px-3 py-2"
                placeholder="Largura"
              />
              <span className="text-retro-muted">×</span>
              <input
                type="number"
                min="16"
                max="7680"
                value={heightInput}
                onChange={(e) => {
                  setHeightInput(e.target.value);
                  setErrors(prev => ({ ...prev, dim: undefined }));
                }}
                className="retro-input w-full px-3 py-2"
                placeholder="Altura"
              />
            </div>
            {errors.dim && (
              <p className="text-retro-error text-xs mt-1">❌ {errors.dim}</p>
            )}
          </div>

          <label className="flex items-start gap-2 cursor-pointer retro-card p-3 border-2 border-retro-line">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!config.normalizeAudio}
              onChange={(e) => setConfig(prev => ({ ...prev, normalizeAudio: e.target.checked }))}
            />
            <span>
              <span className="text-sm text-retro-text block">Normalizar volume</span>
              <span className="text-xs text-retro-muted">
                Deixar perto de 0 dB sem estourar
              </span>
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="p-4 border-t-2 border-retro-line flex gap-3">
          <button
            onClick={handleClose}
            className="retro-button retro-button-secondary flex-1 py-2"
          >
            CANCELAR
          </button>

          <button
            onClick={handleConfirm}
            disabled={!!errors.targetSize || !!errors.dim}
            className={`
              retro-button flex-1 py-2
              ${errors.targetSize || errors.dim ? 'retro-button-disabled' : 'retro-button-success'}
            `}
          >
            🔄 REFAZER CONVERSÃO
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedoConversionModal;